/**
 * Physics System Type Definitions
 * 
 * Uses Babylon.js Physics V2 with Havok plugin
 * Reference: https://doc.babylonjs.com/features/featuresDeepDive/physics
 */

import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

/**
 * Physics engine configuration
 */
export interface PhysicsConfig {
    /** Gravity vector (default: 0, -9.81, 0) */
    gravity: Vector3;
    /** Enable debug visualization */
    debugEnabled: boolean;
    /** Physics timestep (default: 1/60) */
    timestep: number;
    /** Maximum substeps per frame */
    maxSubSteps: number;
}

/**
 * Options for creating a physics body
 */
export interface PhysicsBodyOptions {
    /** Mass of the body (0 = static) */
    mass: number;
    /** Coefficient of restitution (bounciness, 0-1) */
    restitution?: number;
    /** Friction coefficient */
    friction?: number;
    /** Linear damping (air resistance) */
    linearDamping?: number;
    /** Angular damping (rotational resistance) */
    angularDamping?: number;
    /** Start as kinematic (controlled by code, not physics) */
    startAsKinematic?: boolean;
    /** Disable pre-step to improve performance for static bodies */
    disablePreStep?: boolean;
}

/**
 * Shape configuration for physics bodies
 */
export interface PhysicsShapeConfig {
    /** Shape type from Babylon.js */
    type: PhysicsShapeType;
    /** Custom size override (uses mesh bounds if not specified) */
    size?: Vector3;
    /** Mesh for convex hull or mesh shape types */
    mesh?: any;
}

/**
 * Collision groups for filtering
 * Re-exported from core/CollisionGroups for unified collision layer system
 */
import { CollisionGroup } from "../core/CollisionGroups";
export { CollisionGroup };

/**
 * Collision event data
 */
export interface CollisionEvent {
    /** First body in collision */
    bodyA: any;
    /** Second body in collision */
    bodyB: any;
    /** Contact point in world space */
    contactPoint: Vector3;
    /** Contact normal */
    normal: Vector3;
    /** Collision impulse magnitude */
    impulse: number;
}

/**
 * Raycast result
 */
export interface RaycastResult {
    /** Did the ray hit something? */
    hasHit: boolean;
    /** Hit point in world space */
    hitPoint?: Vector3;
    /** Normal at hit point */
    hitNormal?: Vector3;
    /** Distance to hit */
    distance?: number;
    /** The body that was hit */
    body?: any;
}

/**
 * Collision shape configuration for map assets
 */
export interface CollisionShapeConfig {
    /** Shape type */
    type: PhysicsShapeType;
    /** Custom size override */
    size?: Vector3;
    /** Offset from center */
    offset?: Vector3;
    /** Radius for spheres/capsules/cylinders */
    radius?: number;
    /** Height for capsules/cylinders */
    height?: number;
}

/**
 * Ground collision detection result
 */
export interface GroundCheckResult {
    /** Is there ground below? */
    isGrounded: boolean;
    /** Ground height at position */
    groundHeight: number;
    /** Surface normal */
    normal: Vector3;
    /** Distance to ground */
    distance: number;
    /** Slope angle in radians */
    slopeAngle: number;
    /** The mesh that was hit */
    groundMesh?: any;
}

/**
 * Collision layer mask configuration
 */
export interface CollisionLayerConfig {
    /** Collision group this body belongs to */
    group: CollisionGroup;
    /** Collision mask for what this body collides with */
    mask: number;
}

/**
 * Bounding volume types for broad phase collision
 */
export enum BoundingVolumeType {
    /** Axis-aligned bounding box */
    AABB = "aabb",
    /** Bounding sphere */
    SPHERE = "sphere",
    /** Oriented bounding box */
    OBB = "obb",
}

/**
 * Debug visualization options for physics
 */
export interface PhysicsDebugOptions {
    /** Show collision shapes */
    showCollisionShapes: boolean;
    /** Show bounding boxes */
    showBoundingBoxes: boolean;
    /** Show contact points */
    showContactPoints: boolean;
    /** Show velocity vectors */
    showVelocityVectors: boolean;
    /** Show raycasts */
    showRaycasts: boolean;
    /** Color for static bodies */
    staticColor: string;
    /** Color for dynamic bodies */
    dynamicColor: string;
    /** Color for kinematic bodies */
    kinematicColor: string;
    /** Color for triggers/sensors */
    triggerColor: string;
}

/**
 * Physics time configuration for acceleration/deceleration
 */
export interface PhysicsTimeConfig {
    /** Current time scale (1 = normal, 2 = double speed, 0.5 = half speed, 0 = paused) */
    timeScale: number;
    /** Target time scale for smooth transitions */
    targetTimeScale: number;
    /** Acceleration rate (units per second to change time scale) */
    accelerationRate: number;
    /** Deceleration rate (units per second to change time scale) */
    decelerationRate: number;
    /** Minimum allowed time scale */
    minTimeScale: number;
    /** Maximum allowed time scale */
    maxTimeScale: number;
}

/**
 * Time scale presets for common scenarios
 */
export enum TimeScalePreset {
    /** Completely paused (0x) */
    PAUSED = 0,
    /** Slow motion (0.25x) */
    SLOW_MOTION = 0.25,
    /** Half speed (0.5x) */
    HALF_SPEED = 0.5,
    /** Normal speed (1x) */
    NORMAL = 1,
    /** Fast forward (2x) */
    FAST = 2,
    /** Very fast (4x) */
    VERY_FAST = 4,
    /** Ultra fast (10x) */
    ULTRA_FAST = 10,
}

/**
 * Time state snapshot for physics
 */
export interface PhysicsTimeState {
    /** Current effective time scale */
    currentTimeScale: number;
    /** Target time scale being interpolated to */
    targetTimeScale: number;
    /** Whether time is currently accelerating */
    isAccelerating: boolean;
    /** Whether time is currently decelerating */
    isDecelerating: boolean;
    /** Whether physics is paused */
    isPaused: boolean;
    /** Scaled delta time for physics calculations */
    scaledDeltaTime: number;
}

/**
 * Re-export PhysicsShapeType for convenience
 */
export { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
